/**
 * asistenciaConfigService.js
 * Servicio centralizado para gobernar el sistema de marcación de asistencia.
 * Lee la configuración desde Firestore (asistencia-config/{ruc}) y expone
 * funciones de validación que aplican las reglas configuradas.
 *
 * La app móvil debe importar/replicar este servicio para que el admin panel
 * sea la única fuente de verdad del comportamiento de marcación.
 */

import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";

// ─── Caché en memoria por RUC ─────────────────────────────────────────────────
const _cache = {};

/**
 * Lee la configuración de asistencia desde Firestore.
 * Cachea el resultado en memoria durante la sesión activa.
 * @param {string} ruc - RUC de la empresa
 * @param {boolean} forzarRecarga - Si true, ignora el caché
 * @returns {Promise<object>} Configuración completa
 */
export async function cargarConfig(ruc, forzarRecarga = false) {
  if (!forzarRecarga && _cache[ruc]) return _cache[ruc];
  const db = getFirestore();
  const snap = await getDoc(doc(db, "asistencia-config", ruc));
  const config = snap.exists() ? snap.data() : null;
  if (config) _cache[ruc] = config;
  return config;
}

/** Invalida el caché (llamar después de guardar cambios en ConfiguracionAsistencia) */
export function invalidarCache(ruc) {
  if (ruc) delete _cache[ruc];
  else Object.keys(_cache).forEach((k) => delete _cache[k]);
}

// ─── Suscripciones activas (onSnapshot) ───────────────────────────────────────
const _suscripciones = {};

/**
 * Suscribe a cambios en tiempo real de la configuración de asistencia.
 * Cada vez que el admin guarda (y actualiza ultimaActualizacion), el callback
 * se dispara con la nueva config. Ideal para que la app móvil reaccione al instante.
 *
 * @param {string}   ruc      - RUC de la empresa
 * @param {function} callback - fn(config) llamada al detectar un cambio
 * @returns {function} unsubscribe — llamar al desmontar el componente/pantalla
 *
 * @example
 *   // En la app móvil (React Native):
 *   useEffect(() => {
 *     const unsub = suscribirCambios(ruc, (config) => {
 *       setConfig(config);
 *       mostrarToast("Configuración actualizada: " + config.ultimaActualizacion);
 *     });
 *     return () => unsub();
 *   }, [ruc]);
 */
export function suscribirCambios(ruc, callback) {
  // Cancelar suscripción previa para el mismo RUC si existe
  if (_suscripciones[ruc]) {
    _suscripciones[ruc]();
  }

  const db = getFirestore();
  const ref = doc(db, "asistencia-config", ruc);

  const unsubscribe = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) return;
      const config = snap.data();
      // Actualizar caché automáticamente
      _cache[ruc] = config;
      callback(config);
    },
    (error) => {
      console.error(`[asistenciaConfigService] Error en suscripción (${ruc}):`, error);
    }
  );

  _suscripciones[ruc] = unsubscribe;
  return unsubscribe;
}

/**
 * Cancela la suscripción activa para un RUC.
 * Llámalo al cerrar sesión para evitar listeners huérfanos.
 */
export function cancelarSuscripcion(ruc) {
  if (_suscripciones[ruc]) {
    _suscripciones[ruc]();
    delete _suscripciones[ruc];
  }
}

// ─── Resultado estándar de validación ─────────────────────────────────────────
/**
 * @typedef {object} ResultadoValidacion
 * @property {boolean} valida       - Si la marcación es técnicamente válida
 * @property {boolean} bloquear     - Si se debe bloquear la operación
 * @property {string[]} advertencias - Mensajes de advertencia (no bloquean)
 * @property {string[]} errores      - Mensajes de error (bloquean)
 */

function resultado(bloquear = false, advertencias = [], errores = []) {
  return { valida: !bloquear && errores.length === 0, bloquear, advertencias, errores };
}

function aplicarModo(modo, mensaje, advertencias, errores) {
  if (modo === "bloqueo") errores.push(mensaje);
  else if (modo === "advertencia") advertencias.push(mensaje);
  // "ignorar" → no hace nada
}

// ─── 1. Validación de ubicación ───────────────────────────────────────────────
/**
 * Valida si una marcación respeta las reglas de geocerca.
 * @param {object} params
 * @param {number} params.distancia     - Distancia al centro en metros
 * @param {number} params.precision     - Precisión del GPS en metros
 * @param {string} params.tipoMarcacion - "Ingreso" | "Salida" | "Refrigerio_Inicio" | "Refrigerio_Fin"
 * @param {object} config - Configuración completa
 * @returns {ResultadoValidacion}
 */
export function validarUbicacion({ distancia, precision, tipoMarcacion }, config) {
  const advertencias = [];
  const errores = [];
  const reglas = config?.reglas || {};
  const vu = reglas.validacionUbicacion || {};
  const parametros = config?.parametros || {};

  if (!vu.habilitada) return resultado(false, [], []);

  // Precisión GPS insuficiente
  const precisionMin = parametros.precisionMinimaGPS ?? 50;
  if (precision > precisionMin) {
    advertencias.push(`Precisión GPS insuficiente: ${precision}m (mínima: ${precisionMin}m)`);
  }

  // Permitir sin distancia según tipo
  const esRefrigerio = tipoMarcacion?.startsWith("Refrigerio");
  if (esRefrigerio && reglas.permitirRefrigerioSinDistancia) return resultado(false, advertencias, errores);
  if (tipoMarcacion === "Ingreso" && reglas.permitirIngresoSinDistancia) return resultado(false, advertencias, errores);
  if (tipoMarcacion === "Salida" && reglas.permitirSalidaSinDistancia) return resultado(false, advertencias, errores);

  // Distancia fuera del área
  const distanciaMax = parametros.distanciaMaximaPermitida ?? 100;
  const fueraDeArea = distancia > distanciaMax;

  if (fueraDeArea) {
    const msg = `Fuera del área permitida: ${Math.round(distancia)}m (máximo: ${distanciaMax}m)`;
    if (vu.bloquearFueraDeArea && !vu.permitirFueraDeArea) {
      errores.push(msg);
    } else if (vu.alertarFueraDeArea) {
      advertencias.push(msg);
    }
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 2. Detección de tipo de turno ────────────────────────────────────────────
/**
 * Detecta el tipo de turno (diurno/vespertino/nocturno) según la hora de ingreso.
 * @param {number} hora - Hora del día (0-23)
 * @param {object} config - Configuración completa
 * @returns {"diurno"|"vespertino"|"nocturno"}
 */
export function detectarTipoTurno(hora, config) {
  const detector = config?.parametros?.detectorTurnos || {};
  if (!detector.habilitado) return detector.tipoTurnoPorDefecto || "diurno";

  const criterios = detector.criteriosDeteccion || {};

  for (const [tipo, c] of Object.entries(criterios)) {
    const { min, max } = c.rangoEntrada || {};
    if (min == null || max == null) continue;
    if (min <= max) {
      if (hora >= min && hora <= max) return tipo;
    } else {
      // Rango que cruza medianoche (ej. 22–2)
      if (hora >= min || hora <= max) return tipo;
    }
  }

  return detector.tipoTurnoPorDefecto || "diurno";
}

// ─── 3. Validación de horario ─────────────────────────────────────────────────
/**
 * Valida si la hora de una marcación respeta los horarios configurados.
 * @param {object} params
 * @param {string} params.tipoMarcacion - "Ingreso" | "Salida" | ...
 * @param {Date}   params.fechaHora     - Fecha y hora de la marcación
 * @param {string} params.horarioEntrada - "HH:mm" del horario teórico
 * @param {string} params.horarioSalida  - "HH:mm" del horario teórico
 * @param {object} config
 * @returns {ResultadoValidacion}
 */
export function validarHorario({ tipoMarcacion, fechaHora, horarioEntrada, horarioSalida }, config) {
  const advertencias = [];
  const errores = [];
  const vh = config?.reglas?.validacionHorarios || {};
  const lite = config?.lite?.horarios || {};
  const parametros = config?.parametros || {};

  if (!vh.habilitada) return resultado(false, [], []);

  const toleranciaEntrada = parametros.toleranciaMinutosEntrada ?? 15;
  const toleranciaSalida = parametros.toleranciaMinutosSalida ?? 15;

  const hora = fechaHora.getHours();
  const minuto = fechaHora.getMinutes();
  const minutosActual = hora * 60 + minuto;

  const parsearHHMM = (str) => {
    if (!str) return null;
    const [h, m] = str.split(":").map(Number);
    return h * 60 + m;
  };

  if (tipoMarcacion === "Ingreso") {
    const minutosTeórico = parsearHHMM(horarioEntrada || parametros.horarioEntradaPorDefecto);
    if (minutosTeórico != null) {
      if (minutosActual < minutosTeórico - toleranciaEntrada) {
        aplicarModo(lite.entradaTempranaModo || "advertencia", "Entrada registrada antes del horario permitido", advertencias, errores);
      } else if (minutosActual > minutosTeórico + toleranciaEntrada) {
        aplicarModo(lite.entradaTardiaModo || "advertencia", `Entrada tardía: ${minutosActual - minutosTeórico} min después del horario`, advertencias, errores);
      }
    }
  }

  if (tipoMarcacion === "Salida") {
    const minutosTeórico = parsearHHMM(horarioSalida || parametros.horarioSalidaPorDefecto);
    if (minutosTeórico != null) {
      if (minutosActual < minutosTeórico - toleranciaSalida) {
        aplicarModo(lite.salidaTempranaModo || "advertencia", "Salida registrada antes del horario permitido", advertencias, errores);
      } else if (minutosActual > minutosTeórico + toleranciaSalida) {
        aplicarModo(lite.salidaTardiaModo || "advertencia", `Salida tardía: ${minutosActual - minutosTeórico} min después del horario`, advertencias, errores);
      }
    }
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 4. Validación de secuencia ───────────────────────────────────────────────
/**
 * Valida que una nueva marcación siga el orden lógico de la jornada.
 * @param {object[]} marcacionesPrevias - Marcaciones ya registradas en la jornada (ordenadas por tiempo)
 * @param {string}   nuevaTipo          - Tipo de la nueva marcación
 * @param {object}   config
 * @returns {ResultadoValidacion}
 */
export function validarSecuencia(marcacionesPrevias, nuevaTipo, config) {
  const advertencias = [];
  const errores = [];
  const vs = config?.reglas?.validacionSecuencial || {};
  const liteSeq = config?.lite?.secuencia || {};
  const flujos = config?.flujosMarcacion || {};

  if (!vs.habilitada) return resultado(false, [], []);

  const tiposPrevios = marcacionesPrevias.map((m) => m.tipo);
  const secuencia = flujos.secuenciaNormal || ["Ingreso", "Refrigerio_Inicio", "Refrigerio_Fin", "Salida"];

  // Verificar duplicados
  if (!vs.permitirDuplicados && tiposPrevios.includes(nuevaTipo)) {
    aplicarModo(
      liteSeq.duplicadoModo || "advertencia",
      `Ya existe una marcación de tipo "${nuevaTipo}" en esta jornada`,
      advertencias, errores
    );
  }

  // Verificar orden lógico
  if (vs.validarOrdenLogico) {
    const indexNuevo = secuencia.indexOf(nuevaTipo);
    const ultimoTipo = tiposPrevios[tiposPrevios.length - 1];
    const indexUltimo = ultimoTipo ? secuencia.indexOf(ultimoTipo) : -1;

    if (indexNuevo !== -1 && indexNuevo <= indexUltimo && !vs.permitirDuplicados) {
      aplicarModo(
        liteSeq.ordenLogicoModo || "advertencia",
        `Orden incorrecto: "${nuevaTipo}" no puede ir después de "${ultimoTipo}"`,
        advertencias, errores
      );
    }

    // Salida sin haber completado refrigerio
    if (nuevaTipo === "Salida" && tiposPrevios.includes("Refrigerio_Inicio") && !tiposPrevios.includes("Refrigerio_Fin")) {
      if (!liteSeq.priorizarSalidaSobreRefrigerio) {
        aplicarModo(
          liteSeq.salidaSinRefrigerioModo || "advertencia",
          "No se ha registrado el fin de refrigerio",
          advertencias, errores
        );
      }
    }
  }

  // Tiempo mínimo entre marcaciones
  if (marcacionesPrevias.length > 0 && vs.tiempoMinimoEntreMarcaciones) {
    const ultima = marcacionesPrevias[marcacionesPrevias.length - 1];
    const tsUltima = ultima.timestamp instanceof Date ? ultima.timestamp : new Date(ultima.timestamp);
    const diffMin = (Date.now() - tsUltima.getTime()) / 60000;
    if (diffMin < vs.tiempoMinimoEntreMarcaciones) {
      aplicarModo(
        liteSeq.tiempoMinimoEntreMarcacionesModo || "advertencia",
        `Deben pasar al menos ${vs.tiempoMinimoEntreMarcaciones} min entre marcaciones (han pasado ${Math.round(diffMin)} min)`,
        advertencias, errores
      );
    }
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 5. Validación de refrigerio ──────────────────────────────────────────────
/**
 * Valida la duración y orden del refrigerio.
 * @param {Date} inicioRefrigerio - Timestamp de Refrigerio_Inicio
 * @param {Date} finRefrigerio    - Timestamp de Refrigerio_Fin
 * @param {object} config
 * @returns {ResultadoValidacion}
 */
export function validarRefrigerio(inicioRefrigerio, finRefrigerio, config) {
  const advertencias = [];
  const errores = [];
  const liteRef = config?.lite?.refrigerio || {};
  const vr = config?.reglas?.validacionRefrigerio || {};

  if (!vr.habilitada) return resultado(false, [], []);

  const inicio = inicioRefrigerio instanceof Date ? inicioRefrigerio : new Date(inicioRefrigerio);
  const fin = finRefrigerio instanceof Date ? finRefrigerio : new Date(finRefrigerio);

  if (fin <= inicio) {
    aplicarModo(liteRef.ordenModo || "bloqueo", "La hora de fin de refrigerio debe ser posterior al inicio", advertencias, errores);
    return resultado(errores.length > 0, advertencias, errores);
  }

  const duracionMin = (fin - inicio) / 60000;
  const minimoLegal = liteRef.minimoLegalMinutos ?? 45;
  const maximo = liteRef.maximoMinutos ?? 60;

  if (duracionMin === 0) {
    aplicarModo(liteRef.duracionCeroModo || "bloqueo", "La duración del refrigerio no puede ser cero", advertencias, errores);
  } else if (duracionMin < minimoLegal) {
    aplicarModo(
      liteRef.menorAlMinimoModo || "bloqueo",
      `Refrigerio muy corto: ${Math.round(duracionMin)} min (mínimo legal: ${minimoLegal} min)`,
      advertencias, errores
    );
  } else if (duracionMin > maximo) {
    aplicarModo(
      liteRef.mayorAlMaximoModo || "bloqueo",
      `Refrigerio excede el máximo: ${Math.round(duracionMin)} min (máximo: ${maximo} min)`,
      advertencias, errores
    );
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 6. Validación de transiciones (tiempos mínimos entre etapas) ─────────────
/**
 * Valida si se cumplió el tiempo mínimo entre dos etapas de la jornada.
 * @param {"ingresoARefrigerio"|"refrigerioFinASalida"|"ingresoASalida"} transicion
 * @param {Date} desde
 * @param {Date} hasta
 * @param {object} config
 * @returns {ResultadoValidacion}
 */
export function validarTransicion(transicion, desde, hasta, config) {
  const advertencias = [];
  const errores = [];
  const transiciones = config?.lite?.transiciones || {};
  const t = transiciones[transicion];

  if (!t) return resultado(false, [], []);

  const desdeDate = desde instanceof Date ? desde : new Date(desde);
  const hastaDate = hasta instanceof Date ? hasta : new Date(hasta);
  const diffMin = (hastaDate - desdeDate) / 60000;

  if (diffMin < t.minutosMinimos) {
    const labels = {
      ingresoARefrigerio: "entre Ingreso y Refrigerio",
      refrigerioFinASalida: "entre Fin de Refrigerio y Salida",
      ingresoASalida: "entre Ingreso y Salida",
    };
    aplicarModo(
      t.modo || "advertencia",
      `Tiempo insuficiente ${labels[transicion]}: ${Math.round(diffMin)} min (mínimo: ${t.minutosMinimos} min)`,
      advertencias, errores
    );
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 7. Validación de jornada completa ────────────────────────────────────────
/**
 * Valida si la duración total de la jornada cumple con los límites configurados.
 * @param {Date} ingreso  - Timestamp de Ingreso
 * @param {Date} salida   - Timestamp de Salida
 * @param {object} config
 * @returns {ResultadoValidacion}
 */
export function validarDuracionJornada(ingreso, salida, config) {
  const advertencias = [];
  const errores = [];
  const parametros = config?.parametros || {};
  const liteJornada = config?.lite?.jornada || {};

  const ingresoDate = ingreso instanceof Date ? ingreso : new Date(ingreso);
  const salidaDate = salida instanceof Date ? salida : new Date(salida);
  const horasTrabajadas = (salidaDate - ingresoDate) / 3600000;

  const horasMin = parametros.horasMinimasJornada ?? 8;
  const horasMax = parametros.horasMaximasJornada ?? 12;

  if (horasTrabajadas < horasMin) {
    aplicarModo(
      liteJornada.minimaModo || "advertencia",
      `Jornada corta: ${horasTrabajadas.toFixed(1)} h (mínimo: ${horasMin} h)`,
      advertencias, errores
    );
  } else if (horasTrabajadas > horasMax) {
    aplicarModo(
      liteJornada.maximaModo || "advertencia",
      `Jornada excede el máximo: ${horasTrabajadas.toFixed(1)} h (máximo: ${horasMax} h)`,
      advertencias, errores
    );
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 8. Validación de descanso entre jornadas ─────────────────────────────────
/**
 * Valida si el empleado cumplió el descanso mínimo desde su última jornada.
 * @param {Date} finUltimaJornada - Timestamp de Salida de la jornada anterior
 * @param {Date} nuevoIngreso     - Timestamp del nuevo Ingreso
 * @param {object} config
 * @returns {ResultadoValidacion}
 */
export function validarDescansoEntreJornadas(finUltimaJornada, nuevoIngreso, config) {
  const advertencias = [];
  const errores = [];
  const horasDescanso = config?.parametros?.horasDescansoObligatorio ?? 8;

  const finDate = finUltimaJornada instanceof Date ? finUltimaJornada : new Date(finUltimaJornada);
  const inicioDate = nuevoIngreso instanceof Date ? nuevoIngreso : new Date(nuevoIngreso);
  const horasTranscurridas = (inicioDate - finDate) / 3600000;

  if (horasTranscurridas < horasDescanso) {
    errores.push(
      `Descanso insuficiente: ${horasTranscurridas.toFixed(1)} h (mínimo: ${horasDescanso} h entre jornadas)`
    );
  }

  return resultado(errores.length > 0, advertencias, errores);
}

// ─── 9. Obtener mensaje configurado ───────────────────────────────────────────
/**
 * Retorna un mensaje configurado, reemplazando variables como {min} y {max}.
 * @param {string} clave   - Clave del mensaje (ej. "errorDescansoInsuficiente")
 * @param {object} config
 * @param {object} [vars]  - Variables a reemplazar (ej. { min: 45, max: 60 })
 * @returns {string}
 */
export function getMensaje(clave, config, vars = {}) {
  let msg = config?.mensajes?.[clave] || clave;
  Object.entries(vars).forEach(([k, v]) => {
    msg = msg.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
  });
  return msg;
}

// ─── 10. Obtener configuración de botones ─────────────────────────────────────
/**
 * Retorna la configuración visual de los botones de marcación.
 * @param {object} config
 * @returns {object} Map de tipo → { label, color, icon }
 */
export function getBotonesConfig(config) {
  return config?.botones || {
    Ingreso: { label: "Ingresar", color: "#4CAF50", icon: "login" },
    Salida: { label: "Salir", color: "#F44336", icon: "logout" },
    Refrigerio_Inicio: { label: "Inic. Refri", color: "#FF9800", icon: "restaurant" },
    Refrigerio_Fin: { label: "Fin Refri", color: "#FFC107", icon: "restaurant_menu" },
  };
}

// ─── 11. Obtener botones habilitados según estado de jornada ──────────────────
/**
 * Determina qué botones mostrar al empleado según las marcaciones ya realizadas.
 * @param {string[]} tiposRegistrados - Tipos ya marcados en la jornada
 * @param {object}   config
 * @returns {string[]} Lista de tipos habilitados para marcar ahora
 */
export function getBotonesHabilitados(tiposRegistrados, config) {
  const flujos = config?.flujosMarcacion || {};
  const permitirSaltarRefrigerio = flujos.permitirSaltarRefrigerio ?? false;
  const secuencia = flujos.secuenciaNormal || ["Ingreso", "Refrigerio_Inicio", "Refrigerio_Fin", "Salida"];
  const sinRefrigerio = flujos.secuenciaSinRefrigerio || ["Ingreso", "Salida"];

  if (tiposRegistrados.length === 0) return ["Ingreso"];

  const habilitados = [];
  for (const tipo of secuencia) {
    if (!tiposRegistrados.includes(tipo)) {
      habilitados.push(tipo);
      break;
    }
  }

  // Si se permite saltar refrigerio y el siguiente es Refrigerio_Inicio → también ofrecer Salida directa
  if (
    permitirSaltarRefrigerio &&
    habilitados.includes("Refrigerio_Inicio") &&
    !tiposRegistrados.includes("Refrigerio_Inicio")
  ) {
    habilitados.push("Salida");
  }

  return habilitados;
}

// ─── 12. Políticas por centro de trabajo ─────────────────────────────────────
/**
 * Retorna las políticas específicas de un centro para un día de la semana.
 * Las políticas por centro sobreescriben las reglas globales solo para ese centro y día.
 *
 * @param {string} nombreCentro - Nombre exacto del centro (ej. "COOR_UO PKM_SEDE AREQUIPA")
 * @param {string} diaSemana    - Día en español minúscula: "lunes"|"martes"|"miercoles"|"jueves"|"viernes"|"sabado"|"domingo"
 * @param {object} config       - Configuración completa
 * @returns {object} Políticas del centro para ese día (objeto vacío si no hay configuración)
 *
 * @example
 *   const politica = getPoliticaCentro("COOR_UO PKM_SEDE AREQUIPA", "sabado", config);
 *   if (politica.permitirSalidaSinRefrigerio) {
 *     // omitir validación de refrigerio para este centro los sábados
 *   }
 */
export function getPoliticaCentro(nombreCentro, diaSemana, config) {
  return config?.lite?.politicasPorCentro?.[nombreCentro]?.[diaSemana] || {};
}

/**
 * Verifica si una política específica está activa para un centro y día.
 * Shorthand para evitar encadenamiento largo en la app.
 *
 * @param {string} nombreCentro
 * @param {string} diaSemana
 * @param {string} flagClave    - ej. "permitirSalidaSinRefrigerio"
 * @param {object} config
 * @returns {boolean}
 */
export function tienePolitica(nombreCentro, diaSemana, flagClave, config) {
  return !!getPoliticaCentro(nombreCentro, diaSemana, config)[flagClave];
}

/**
 * Retorna el día de la semana en español (minúscula) a partir de un objeto Date.
 * Útil para pasarlo directamente a getPoliticaCentro / tienePolitica.
 *
 * @param {Date} fecha
 * @returns {"lunes"|"martes"|"miercoles"|"jueves"|"viernes"|"sabado"|"domingo"}
 */
export function getDiaSemana(fecha) {
  const dias = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];
  return dias[(fecha instanceof Date ? fecha : new Date(fecha)).getDay()];
}

